import React, { useState, useEffect, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Chip
} from "@mui/material";
import {
  Edit as EditIcon,
  DeleteOutlined as DeleteOutlineIcon
} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";

import TableRowSkeleton from "../TableRowSkeleton";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

const CATEGORY_STYLE = {
  MARKETING: { label: "Marketing", bg: "#ed6c02", fg: "#fff" },
  UTILITY: { label: "Utilidad", bg: "#0288d1", fg: "#fff" },
  AUTHENTICATION: { label: "Autenticación", bg: "#7b1fa2", fg: "#fff" },
};

const categoryStyle = (category) =>
  CATEGORY_STYLE[String(category || "").toUpperCase()] || {
    label: category || "—",
    bg: "#9e9e9e",
    fg: "#fff",
  };

const STATUS_STYLE = {
  APPROVED: { label: "APROBADA", bg: "#2e7d32", fg: "#fff" },
  PENDING: { label: "PENDIENTE", bg: "#f9a825", fg: "#3e2723" },
  REJECTED: { label: "RECHAZADA", bg: "#d32f2f", fg: "#fff" },
  PAUSED: { label: "PAUSADA", bg: "#ef6c00", fg: "#fff" },
  DISABLED: { label: "DESACTIVADA", bg: "#616161", fg: "#fff" },
  PENDING_DELETION: { label: "POR ELIMINAR", bg: "#616161", fg: "#fff" },
};

const statusStyle = (status) =>
  STATUS_STYLE[String(status || "").toUpperCase()] || {
    label: status || "—",
    bg: "#9e9e9e",
    fg: "#fff",
  };

function TemplateMessageTable({ messages, showLoading, editMessage, deleteMessage, readOnly, costByTemplate, ocultarMarcaCampana }) {
  const showCosts = !!costByTemplate;
  const money = (n) =>
    typeof n === "number" ? `${n.toFixed(n < 1 ? 4 : 2)} US$` : "—";
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const { whatsApps } = useContext(WhatsAppsContext);

  const connectionName = useMemo(() => {
    const map = {};
    (whatsApps || []).forEach((w) => {
      map[String(w.id)] = w.name;
    });
    return map;
  }, [whatsApps]);

  useEffect(() => {
    if (Array.isArray(messages)) {
      setRows(messages);
    }
    if (showLoading !== undefined) {
      setLoading(showLoading);
    }
  }, [messages, showLoading]);

  const handleEdit = (message) => {
    editMessage(message);
  };

  const handleDelete = (message) => {
    deleteMessage(message);
  };

  const renderStatus = (message) => {
    const st = statusStyle(message.status);
    const chip = (
      <Chip
        label={st.label}
        size="small"
        sx={{
          backgroundColor: st.bg,
          color: st.fg,
          fontWeight: 600,
          height: 22,
        }}
      />
    );

    return message.statusReason ? (
      <Tooltip title={message.statusReason}>{chip}</Tooltip>
    ) : (
      chip
    );
  };

  const translateLanguage = (text = "") => {
    const normalized = text.toLowerCase();
    if (normalized.startsWith("es")) return "ES";
    if (normalized.startsWith("en")) return "EN";
    if (normalized.startsWith("pt")) return "PT";
    return text;
  };

  const renderRows = () => {
    return rows.map((message) => {
      const conn =
        connectionName[String(message.whatsappId)] ||
        (message.whatsappId ? `#${message.whatsappId}` : "—");

      return (
        <TableRow key={message.id}>
          <TableCell align="left" style={{ fontWeight: 500 }}>
            {message.name}
            {message.forCampaign === true && !ocultarMarcaCampana && (
              <Chip
                label="Campañas"
                size="small"
                variant="outlined"
                color="primary"
                style={{ marginLeft: 6, height: 20, fontSize: 11 }}
              />
            )}
          </TableCell>

          <TableCell align="center">{conn}</TableCell>

          <TableCell align="center">{renderStatus(message)}</TableCell>

          <TableCell align="center">
            {(() => {
              const st = categoryStyle(message.category);
              const chip = (
                <Chip
                  label={st.label}
                  size="small"
                  sx={{
                    backgroundColor: st.bg,
                    color: st.fg,
                    fontWeight: 600,
                    height: 22,
                  }}
                />
              );
              return message.statusReason ? (
                <Tooltip title={message.statusReason}>{chip}</Tooltip>
              ) : (
                chip
              );
            })()}
          </TableCell>

          <TableCell align="center">{translateLanguage(message.language)}</TableCell>

          <TableCell
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "260px",
            }}
            align="left"
          >
            {message.bodyText}
          </TableCell>

          {showCosts && (() => {
            const m = costByTemplate[String(message.id)];
            return (
              <>
                <TableCell align="center">{m?.sent ?? 0}</TableCell>
                <TableCell align="center">{m?.delivered ?? 0}</TableCell>
                <TableCell align="center">{m?.read ?? 0}</TableCell>
                <TableCell align="right" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  {money(m?.cost ?? 0)}
                </TableCell>
              </>
            );
          })()}

          {!readOnly && (
            <TableCell align="center">
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => handleEdit(message)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Eliminar">
                <IconButton size="small" onClick={() => handleDelete(message)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
          )}
        </TableRow>
      );
    });
  };

  const columnsCount = (readOnly ? 6 : 7) + (showCosts ? 4 : 0);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell align="left"><strong>Nombre</strong></TableCell>
          <TableCell align="center"><strong>Conexión</strong></TableCell>
          <TableCell align="center"><strong>Estado</strong></TableCell>
          <TableCell align="center"><strong>Categoría</strong></TableCell>
          <TableCell align="center"><strong>Idioma</strong></TableCell>
          <TableCell align="center"><strong>Mensaje</strong></TableCell>
          {showCosts && (
            <>
              <TableCell align="center"><strong>Enviados</strong></TableCell>
              <TableCell align="center"><strong>Entregados</strong></TableCell>
              <TableCell align="center"><strong>Leídos</strong></TableCell>
              <TableCell align="right"><strong>Gasto</strong></TableCell>
            </>
          )}
          {!readOnly && <TableCell align="center"><strong>Acción</strong></TableCell>}
        </TableRow>
      </TableHead>

      <TableBody>
        {loading ? (
          <TableRowSkeleton columns={columnsCount} />
        ) : (
          renderRows()
        )}
      </TableBody>
    </Table>
  );
}

TemplateMessageTable.propTypes = {
  messages: PropTypes.array.isRequired,
  showLoading: PropTypes.bool,
  editMessage: PropTypes.func.isRequired,
  deleteMessage: PropTypes.func.isRequired,
  costByTemplate: PropTypes.object,
  readOnly: PropTypes.bool,
  ocultarMarcaCampana: PropTypes.bool
};

export default TemplateMessageTable;
